import {
  getDirectRelationship,
  isTemplateStatus,
  type TaskInterval,
  type Template,
  type TemplateItem,
} from '@cognite/apm-client';
import type { DirectRelationReference } from '@cognite/sdk';

import { mapCdmMeasurementsToMeasurements } from '../shared-utils/map-cdm-measurements-to-measurements';
import { sortOrderAscending } from '../shared-utils/sort-order-ascending';
import { extractSourceableFields } from '../shared-utils/sourceable-utils';

import { cdmTemplateServiceUtilsContext } from './cdm-template-service-utils.context';
import type {
  CdmTemplateItemUpsertType,
  CdmTemplateResponseType,
  CdmTemplateUpsertType,
  ScheduleResponseType,
} from './cdm-types';

export const isMappableCdmTemplateResponse = (
  cdmTemplate: CdmTemplateResponseType
): boolean =>
  cdmTemplate.status == null || isTemplateStatus(cdmTemplate.status);

const mapSchedulesResponseToTaskIntervals = (schedulesResponse: {
  items: ScheduleResponseType[];
}): TaskInterval[] => {
  const schedules = schedulesResponse.items.map((schedule) => ({
    ...schedule,
    templateItems: schedule.templateItems?.items ?? undefined,
  }));
  // Sort descending by sourceCreatedTime
  const schedulesDescending = schedules.sort(
    (a, b) =>
      new Date(b.sourceCreatedTime ?? 0).getTime() -
      new Date(a.sourceCreatedTime ?? 0).getTime()
  );
  return schedulesDescending;
};

const mapSchedulesToTemplateItemSchedules = (
  templateItemExternalId: string,
  schedules?: { items: ScheduleResponseType[] }
): TaskInterval[] => {
  if (!schedules) return [];
  return schedules.items
    .filter((schedule) =>
      schedule.templateItems?.items.some(
        (templateItem) => templateItem.externalId === templateItemExternalId
      )
    )
    .map((schedule) => ({
      ...schedule,
      templateItems: schedule.templateItems?.items,
    }));
};

export const mapTemplateResponseToTemplate = (
  cdmTemplate: CdmTemplateResponseType
): Template => {
  const status = cdmTemplate.status;
  if (status != null && !isTemplateStatus(status)) {
    cdmTemplateServiceUtilsContext.captureMessage(
      'Invalid template status when mapping CDM template response',
      {
        level: 'warning',
        tags: {
          component: 'cdm-template-service-utils',
          operation: 'mapTemplateResponseToTemplate',
        },
        extra: {
          externalId: cdmTemplate.externalId,
          space: cdmTemplate.space,
          status,
        },
      }
    );
    throw new Error(
      `Invalid template status "${String(status)}" for template ${cdmTemplate.externalId} (space: ${cdmTemplate.space ?? 'unknown'})`
    );
  }

  return {
    ...cdmTemplate,
    assignedTo: cdmTemplate.assignedTo ?? undefined,
    isArchived: cdmTemplate.isArchived ?? undefined,
    status: status ?? undefined,
    schedules: cdmTemplate.schedules
      ? mapSchedulesResponseToTaskIntervals(cdmTemplate.schedules)
      : undefined,
    templateItems: cdmTemplate.templateItems?.items
      ? cdmTemplate.templateItems.items
          .map((templateItem) => {
            return {
              ...templateItem,
              measurements: templateItem.measurements?.items
                ? mapCdmMeasurementsToMeasurements(
                    templateItem.measurements.items
                  )
                : undefined,
              schedules: mapSchedulesToTemplateItemSchedules(
                templateItem.externalId,
                cdmTemplate.schedules
              ),
            };
          })
          .sort(sortOrderAscending)
      : undefined,
    solutionTags: cdmTemplate.solutionTags?.items ?? undefined,
  };
};

/**
 * Maps a Template to a CdmTemplateUpsertType for upsert, stripping
 * fields that are not part of the instance write:
 * - `createdBy`, `updatedBy` — deprecated InFieldUser fields excluded to prevent
 *   PII from being written to data model storage (see extractSourceableFields).
 * - `createdTime`, `lastUpdatedTime` — server-managed; excluded from writes.
 * - `templateItems` — written separately via the template items upsert service.
 * @param template - The template to map.
 * @param rootLocation - The root location of the template.
 * @param instanceSpace - The space of the app instance.
 * @returns A payload ready for writing to the Cognite data model via the sourceable upsert service.
 */
export const mapTemplateToUpsertType = (
  template: Template,
  rootLocation: DirectRelationReference,
  instanceSpace: string
): CdmTemplateUpsertType => {
  return {
    externalId: template.externalId,
    title: template.title,
    status: template.status,
    assignedTo: template.assignedTo,
    isArchived: template.isArchived,
    rootLocation:
      template.rootLocation && template.rootLocation.space
        ? {
            space: template.rootLocation.space,
            externalId: template.rootLocation.externalId,
          }
        : rootLocation,
    // Preserve empty arrays to allow users to delete all solution tags
    // Empty array signals API to clear tags, undefined means no change
    solutionTags: template.solutionTags
      ?.filter((tag) => tag && tag.externalId)
      .map((tag) => ({
        externalId: tag.externalId,
        space: instanceSpace,
      })),
    ...extractSourceableFields(template),
  };
};

/**
 * Maps a TemplateItem to a CdmTemplateItemUpsertType for upsert, stripping
 * fields that are not part of the instance write:
 * - `createdBy`, `updatedBy` — deprecated InFieldUser fields excluded to prevent
 *   PII from being written to data model storage (see extractSourceableFields).
 * - `createdTime`, `lastUpdatedTime` — server-managed; excluded from writes.
 * - `schedules`, `measurements`, `conditionalActions` — written separately via the edge relationships service.
 * @param templateItem - The template item to map.
 * @returns A payload ready for writing to the Cognite data model via the sourceable upsert service.
 */
export const mapTemplateItemToUpsertType = (
  templateItem: TemplateItem
): CdmTemplateItemUpsertType => {
  return {
    externalId: templateItem.externalId,
    title: templateItem.title,
    description: templateItem.description,
    labels: templateItem.labels,
    order: templateItem.order,
    isArchived: templateItem.isArchived,
    asset: getDirectRelationship(templateItem.asset),
    ...extractSourceableFields(templateItem),
  };
};
